// client/src/utils/marketerProjectCache.js

const CACHE_PREFIX = 'MARKETER_PROJECT_';
const CACHE_TTL = 5 * 60 * 1000; // 5분

// sessionStorage 헬퍼
const ss = typeof window !== 'undefined' ? window.sessionStorage : null;

function cacheSet(key, value) {
  if (!ss) return;
  try {
    ss.setItem(key, JSON.stringify({
      data: value,
      timestamp: Date.now(),
      uid: window.__ME__?.id  // ✅ 사용자 ID 함께 저장
    }));
  } catch (e) {
    console.error('캐시 저장 실패:', e);
  }
}

function cacheGet(key) {
  if (!ss) return null;
  try {
    const cached = ss.getItem(key);
    if (!cached) return null;
    
    const { data, timestamp, uid } = JSON.parse(cached);
    
    // ✅ TTL 체크
    if (Date.now() - timestamp > CACHE_TTL) {
      ss.removeItem(key);
      return null;
    }
    
    // ✅✅✅ 사용자 검증 (수정!)
    const currentUid = window.__ME__?.id;
    
    // ✅ currentUid가 없으면 null 반환 (로그인 전이므로 캐시 사용 불가)
    if (!currentUid) {
      return null;
    }
    
    // ✅ currentUid가 있는데 캐시 uid와 다르면 삭제
    if (uid && uid !== currentUid) {
      console.warn('캐시 사용자 불일치, 삭제:', key);
      ss.removeItem(key);
      return null;
    }
    
    return data;
  } catch (e) {
    console.error('캐시 조회 실패:', e);
    return null;
  }
}

// 프로젝트 목록 캐시
export function cacheProjectList(uid, projects) {
  cacheSet(`${CACHE_PREFIX}LIST:${uid}`, projects);
}

export function readProjectList(uid) {
  return cacheGet(`${CACHE_PREFIX}LIST:${uid}`);
}

// ✅ igVerified는 캐시에 저장하지 않음 (노출 기한: 선정 윈도우·90일 — 캐시로 기한 초과 노출 원천 차단)
//    저장·읽기 양쪽 방어: 배포 창에서 구 클라이언트가 저장한 캐시(TTL 최대 5분)를 신 클라이언트가 소비하는 경로까지 차단
function stripIgVerified(applications) {
  if (!Array.isArray(applications)) return applications;
  return applications.map((app) => {
    if (app && typeof app === 'object' && 'igVerified' in app) {
      const { igVerified, ...rest } = app;
      return rest;
    }
    return app;
  });
}

// 지원자 목록 캐시 (필터 포함)
// ✅ ':ic' suffix — includeCanceled=1 응답 전용 키. 구 형식 키(취소 행 없는 응답)와 분리해
//    배포 전후 혼용 시 취소 섹션 미표시/이중 표시 버그 차단 (FEATURE_IG_SNS_INTEGRATION 구간 6)
export function cacheApplications(projectId, applications, filter = 'all') {
  cacheSet(`${CACHE_PREFIX}APP:${projectId}:${filter}:ic`, stripIgVerified(applications));
}

export function readApplications(projectId, filter = 'all') {
  const data = cacheGet(`${CACHE_PREFIX}APP:${projectId}:${filter}:ic`);
  return Array.isArray(data) ? stripIgVerified(data) : data;
}

// 캐시 초기화
export function clearMarketerCache(uid) {
  if (!ss) return;
  try {
    const keys = Object.keys(ss);
    keys.forEach(key => {
      // ✅ MARKETER_PROJECT_로 시작하는 모든 캐시 삭제
      if (key.startsWith(CACHE_PREFIX)) {
        ss.removeItem(key);
      }
    });
  } catch (e) {
    console.error('캐시 초기화 실패:', e);
  }
}

// 백그라운드 로드
export async function warmMarketerProjects(uid) {
  if (!uid) return;
  
  try {
    // 1. 프로젝트 목록 조회
    const res = await fetch('/api/marketer/projects/with-applications', {
      credentials: 'include'
    });
    const data = await res.json();
    
    if (res.ok && data.ok && data.projects) {
      cacheProjectList(uid, data.projects);
      
      // 2. 첫 번째 프로젝트의 지원자 목록 조회 (즉시 실행)
      // ✅ filter=all과 filter=pending 모두 캐싱
      if (data.projects.length > 0) {
        const firstProjectId = data.projects[0].id;
        
        try {
          // filter=all 캐싱 (includeCanceled=1 — 선정 화면 취소 섹션용, FEATURE_IG_SNS_INTEGRATION 구간 6)
          const appResAll = await fetch(
            `/api/marketer/projects/${firstProjectId}/applications?filter=all&includeCanceled=1`,
            { credentials: 'include' }
          );
          const appDataAll = await appResAll.json();
          
          if (appResAll.ok && appDataAll.ok) {
            cacheApplications(firstProjectId, appDataAll.applications, 'all');
          }

          // filter=pending 캐싱 (백그라운드)
          const appResPending = await fetch(
            `/api/marketer/projects/${firstProjectId}/applications?filter=pending`,
            { credentials: 'include' }
          );
          const appDataPending = await appResPending.json();
          
          if (appResPending.ok && appDataPending.ok) {
            cacheApplications(firstProjectId, appDataPending.applications, 'pending');
          }
        } catch (e) {
          console.error('지원자 목록 백그라운드 로드 실패:', e);
        }
      }
    }
  } catch (e) {
    console.error('프로젝트 목록 백그라운드 로드 실패:', e);
  }
}

